import { getFocusable } from '../utils/getFocusable';
import { nextFocusable } from '../utils/nextFocusable';
import { isPrintableChar } from '../utils/isPrintableChar';
import { isInputable } from '../utils/isInputable';
import { isSet } from '../utils/isSet';

class Dropdown {

    /** @type {Object} */ 
    #eventDetail;

    /** @type {DropdownEvents} */
    #events;

    /** @type {String[]} */ 
    #firstChars; 

    /** @type {Number} */
    #animationOpenCount;

    /** @type {Number} */
    #animationCloseCount;

    /**
     * @param  {HTMLElement} root The DOM element to initialise on
     * @param  {DropdownDefault} opts Optional params to modify functionality
     */
    constructor(root, opts) {
        /** @type {DropdownDefault} */
        const DEFAULTS = {
            onBeforeOpen: () => {},
            onAfterOpen: () => {},
            onBeforeClose: () => {},
            onAfterClose: () => {},
            onSelection: () => {},
            // @ts-ignore
            openClass: 'is-open',
            // @ts-ignore
            focusableElements: window.focusableElements,
            updateOnItemClick: false,
            updateSelector: null,
            closeOnBackgroundClick: true,
            openOnTriggerClick: true,
            openOnTriggerKeyup: true,
            openOnTriggerHover: false,
            focusOnItemMouseOver: true,
            closeOnItemClick: false,
            closeOnLastItemBlur: false,
            loopItemFocus: true,
            arrowNavigation: true,
            tabNavigation: false,
            awaitOpenAnimation: false,
            awaitCloseAnimation: false,
            openOnArrowKeydown: true,
            setFocusOnOpen: true,
        }

        /** @type {DropdownDefault} */
        this.options = Object.assign(DEFAULTS, opts);

        /** @type {HTMLElement} */
        this.root = root;

        this.#eventDetail = {
            detail: {
                component: this 
            },
            cancelable: true
        };

        this.#events = {
            initialised: new CustomEvent( 'dropdowninit', this.#eventDetail ),
            initialisedItems: new CustomEvent( 'dropdowninititems', this.#eventDetail ),
            destroyed: new CustomEvent( 'dropdowndestroyed', this.#eventDetail ),
            beforeOpen: new CustomEvent( 'dropdownbeforeopen', this.#eventDetail ),
            afterOpen: new CustomEvent( 'dropdownafteropen', this.#eventDetail ),
            beforeClose: new CustomEvent( 'dropdownbeforeclose', this.#eventDetail ),
            afterClose: new CustomEvent( 'dropdownafterclose', this.#eventDetail ),
            onSelection: new CustomEvent( 'dropdownselection', this.#eventDetail )
        };

        this.#firstChars = [];

        this.#animationOpenCount = 0;

        this.#animationCloseCount = 0;

        this.init();
    }

    init(){
        this.isOpen = false;

        /** @type {HTMLElement|null} */
        this.trigger = this.root.querySelector('[aria-controls]');
        if(!this.trigger) return console.warn('Trigger element does not exist within: ', this.root);

        /** @type {string|null} */
        const ariaControls = this.trigger.getAttribute('aria-controls');
        if (!ariaControls) return console.warn(`aria-controls attribute is null for: `, this.trigger);

        /** @type {HTMLElement|null} */
        this.dropdown = document.getElementById(ariaControls);
        if(!this.dropdown) return console.warn('Dropdown element does not exist');
    
        // Bind Node Listeners
        this.onTriggerKeydownBound = this.#onTriggerKeydown.bind(this);
        this.onTriggerClickBound = this.#onTriggerClick.bind(this);
        this.onTriggerKeyupBound = this.#onTriggerKeyup.bind(this);
        this.onTriggerHoverBound = this.#onTriggerHover.bind(this);

        this.onRootFocusInBound = this.#onRootFocusin.bind(this);
        this.onRootFocusOutBound = this.#onRootFocusout.bind(this);

        // Bind Window Listeners
        this.onBackgroundMousedownBound = this.#onBackgroundMousedown.bind(this);
        this.checkBoundingBoxBound = this.checkBoundingBox.bind(this);
        this.updateFocusableElmsBound = this.updateFocusableElms.bind(this);

        this.addElementListeners();
        
        // @ts-ignore
        this.root.classList.add('has-initialised');
        
        window.dispatchEvent(this.#events.initialised);
        this.root.dispatchEvent(this.#events.initialised);
    }


    destroy(){
        this.closeDropdown();
        this.removeGlobalListeners();
        this.removeElementListeners();

        // @ts-ignore
        this.root.classList.remove('has-initialised');

        window.dispatchEvent(this.#events.destroyed);
        this.root.dispatchEvent(this.#events.destroyed);
    }


    initDropdownItems() {
        if (!this.dropdown) return;

        /** @type {Node[]} */
        this.dropdownItems = [];

        /** @type {String[]} */
        this.#firstChars = [];

        /** @type {Function[]} */
        this.itemKeydownHandlers = [];

        /** @type {Function[]} */
        this.itemMouseoverHandlers = [];

        /** @type {Function[]} */
        this.itemClickHandlers = [];

        const focusableElements = this.options.focusableElements;
        focusableElements.push('[role=menuitem]');

        this.dropdownItems = getFocusable(this.dropdown, focusableElements);
        if (!this.dropdownItems) return;

        const itemKeydownHandler = this.#onItemKeydown.bind(this);
        const itemMouseoverHandler = this.#onItemMouseover.bind(this);
        const itemClickHandler = this.#onItemClick.bind(this);

        this.dropdownItems.forEach((item) => {
            item.tabIndex = -1;

            if(item.innerText){
                const firstChar = item.innerText.trim()[0].toLowerCase();
                if(firstChar && this.#firstChars) this.#firstChars.push(firstChar);
            }

            item.addEventListener('click', itemClickHandler);
            if(this.itemClickHandlers) this.itemClickHandlers.push(itemClickHandler);

            item.addEventListener('mouseover', itemMouseoverHandler);
            if(this.itemMouseoverHandlers) this.itemMouseoverHandlers.push(itemMouseoverHandler);

            item.addEventListener('keydown', itemKeydownHandler);
            if(this.itemKeydownHandlers) this.itemKeydownHandlers.push(itemKeydownHandler);

            /** @type {Node|null} */
            if (!this.firstItem) this.firstItem = item;

            /** @type {Node|null} */
            this.lastItem = item;
        });

        this.root.dispatchEvent(this.#events.initialisedItems);
    }

    dispatchStateUpdateEvent() {
        // @ts-ignore
        //window.dispatchEvent();
    }

    /**
     * Adds bound method window events 
     */
    addGlobalListeners() {
        if(this.onBackgroundMousedownBound) window.addEventListener('mousedown', this.onBackgroundMousedownBound);
        if(this.updateFocusableElmsBound) window.addEventListener('stateupdate', this.updateFocusableElmsBound);
        if(this.checkBoundingBoxBound) window.addEventListener('resize', this.checkBoundingBoxBound);
        if(this.checkBoundingBoxBound) document.addEventListener('scroll', this.checkBoundingBoxBound);
    }

    /**
     * Removes bound methods window events 
     */
    removeGlobalListeners() {
        if(this.onBackgroundMousedownBound) window.removeEventListener('mousedown', this.onBackgroundMousedownBound);
        if(this.updateFocusableElmsBound) window.removeEventListener('stateUpdate', this.updateFocusableElmsBound);
        if(this.checkBoundingBoxBound) window.removeEventListener('resize', this.checkBoundingBoxBound);
        if(this.checkBoundingBoxBound) document.removeEventListener('scroll', this.checkBoundingBoxBound);
    }


    /**
     * Adds bound methods to the elements
     */
    addElementListeners() {
        if (!this.trigger || !this.root) return;

        if(this.onTriggerKeydownBound) this.trigger.addEventListener( 'keydown', this.onTriggerKeydownBound);
        if(this.onTriggerClickBound) this.trigger.addEventListener( 'click', this.onTriggerClickBound);
        if(this.onTriggerKeyupBound) this.trigger.addEventListener( 'keyup', this.onTriggerKeyupBound);
        if(this.onTriggerHoverBound) this.trigger.addEventListener( 'mouseover', this.onTriggerHoverBound);

        if(this.onRootFocusInBound) this.root.addEventListener( 'focusin', this.onRootFocusInBound);
        if(this.onRootFocusOutBound) this.root.addEventListener( 'focusout', this.onRootFocusOutBound);
    }
    

    /**
     * Adds bound methods to the domNode elements
     */
    removeElementListeners() {
        if (!this.trigger) return;

        if(this.onTriggerKeydownBound) this.trigger.removeEventListener( 'keydown', this.onTriggerKeydownBound);
        if(this.onTriggerClickBound) this.trigger.removeEventListener( 'click', this.onTriggerClickBound);
        if(this.onTriggerKeyupBound) this.trigger.removeEventListener( 'keyup', this.onTriggerKeyupBound);
        if(this.onTriggerHoverBound) this.trigger.removeEventListener( 'mouseover', this.onTriggerHoverBound);
        if(this.onRootFocusInBound) this.root.removeEventListener( 'focusin', this.onRootFocusInBound);
        if(this.onRootFocusOutBound) this.root.removeEventListener( 'focusout', this.onRootFocusOutBound);
    }


    /**
     * Updates available focusable elements inside the dropdown
     */
    updateFocusableElms(){
        this.initDropdownItems();
    }

    /**
     * Sets focus to a menu item.
     * @param  {Node} targetItem New target menu item
     */
    setFocusToItem(targetItem) {
        if (!this.dropdownItems) return;

        this.dropdownItems.forEach((item) => {
            if (item === targetItem) {
                item.tabIndex = 0;
                targetItem.focus();
            } else {
                item.tabIndex = -1;
            }
        })
    }


    /**
     * Sets focus to the first item in the dropdown menu
     */
    setFocusToFirstItem() {
        if(!this.firstItem) return;

        this.setFocusToItem(this.firstItem);
    }


    /**
     * Sets focus to the last item in the dropdown menu
     */
    setFocusToLastItem() {
        if(!this.lastItem) return;

        this.setFocusToItem(this.lastItem);
    }

    /**
     * Sets focus to the next menu item
     * @param    {Node} currentItem Currently focused item within the dropdown menu
     * @param   {"forward"|"backward"} movement Moving focus forward or backward
     * @returns  {Node|undefined} The newly focused item within the dropdown menu
     */
    #incrementFocus(currentItem, movement) {
        if(!this.dropdownItems || !this.firstItem || !this.lastItem) return currentItem;

        // @ts-ignore
        nextFocusable(
            currentItem,
            this.dropdownItems,
            this.firstItem,
            this.lastItem,
            movement,
            this.options.loopItemFocus,
            (/** @type {HTMLElement|Node} */ current) => this.setFocusToItem(current),
            () => {
                if(!this.options.closeOnLastItemBlur) return false;
                this.closeDropdown();
                return true;
            },
        );
    }


    /**
     * Sets focus by the the first character of a menu item
     * @param  {HTMLElement} currentItem Currently focused item within the dropdown menu
     * @param  {string} char The character to base the focus on
     */
    #setFocusByFirstChar(currentItem, char) {
        if (char.length > 1) return;
        if(!this.dropdownItems || !this.#firstChars) return;

        char = char.toLowerCase();

        let start = this.dropdownItems.indexOf(currentItem) + 1;
        if (start >= this.dropdownItems.length) start = 0;

        let index = this.#firstChars.indexOf(char, start);
        if (index === -1) index = this.#firstChars.indexOf(char, 0);

        if (index > -1) this.setFocusToItem(this.dropdownItems[index]);
    }


    /**
     * Handle any when an opening animation ends within or on the this.domNode
     * @param {AnimationEvent} e AnimationEvent object
     */
    #handleAnimationOpeningEnd(e){
        if(!this.dropdown) return;

        if( e.target === this.root || this.root.contains(e.target) ){
            if(this.dropdown.contains(e.target) && e.target !== this.dropdown ) return;
            
            this.#animationOpenCount--;

            if (this.#animationOpenCount !== 0) return;

            // @ts-ignore
            this.root.classList.remove('is-animating', 'is-animating--open');
            this.isOpen = true;
            this.isOpening = false;
            this.isAnimating = false;
            this.root.dispatchEvent(this.#events.afterOpen);
            this.options.onAfterOpen(this, e);

            if(this.handleAnimationOpeningStartBound) document.removeEventListener('animationstart', this.handleAnimationOpeningStartBound);
            if(this.handleAnimationOpeningEndBound) document.removeEventListener('animationend', this.handleAnimationOpeningEndBound);
        }
    }


    /**
     * Handle when an opening animation starts within or on the this.domNode
     * @param {AnimationEvent} e AnimationEvent object
     */
    #handleAnimationOpeningStart(e){
        if(!this.dropdown) return;

        if( e.target === this.root || this.root.contains(e.target) ){
            if( this.dropdown.contains(e.target) && e.target !== this.dropdown ) return;
            this.#animationOpenCount++;
        }
    }


    /**
     * Handle when an opening animation gets cancelled within or on the this.domNode
     * @param {AnimationEvent} e AnimationEvent object
     */
    #handleAnimationOpeningCancel(e){
        if(!this.dropdown) return;

        this.#animationOpenCount = 0;
        this.#animationCloseCount = 0;

        this.trigger.setAttribute('aria-expanded', 'false');
        this.dropdown.setAttribute('aria-hidden', 'true');
        this.root.classList.remove(this.options.openClass);
        this.root.classList.remove('is-animating', 'is-animating--open');
        this.root.dispatchEvent(this.#events.afterOpen);
        this.options.onAfterOpen(this, e);
        this.removeGlobalListeners();
        this.isOpen = false;
        this.isOpening = false;
        this.isAnimating = false;
        if(this.handleAnimationOpeningStartBound) document.removeEventListener('animationstart', this.handleAnimationOpeningStartBound);
        if(this.handleAnimationOpeningEndBound) document.removeEventListener('animationend', this.handleAnimationOpeningEndBound);
        if(this.handleAnimationOpeningCancelBound) document.removeEventListener('animationcancel', this.handleAnimationOpeningCancelBound);
    }


    /**
     * Opens the dropdown
     * @param  {Event|KeyboardEvent|null} e The event that triggered this method
     */
    openDropdown(e = null) {
        if(!this.trigger || !this.dropdown) return;
        if(this.isAnimating || this.isClosing) return;

        if(this.root.classList.contains('is-closing')) this.#handleAnimationClosingCancel(e);

        this.#animationOpenCount = 0;
        this.#animationCloseCount = 0;

        const beforeOpenValue = this.root.dispatchEvent(this.#events.beforeOpen);
        if (!beforeOpenValue) return;

        this.options.onBeforeOpen(this, e);
        
        this.trigger.setAttribute('aria-expanded', 'true');
        this.dropdown.setAttribute('aria-hidden', 'false');
        this.root.classList.add(this.options.openClass);
        this.addGlobalListeners();

        if (this.options.awaitOpenAnimation) {
            this.handleAnimationOpeningStartBound = this.#handleAnimationOpeningStart.bind(this);
            this.handleAnimationOpeningEndBound = this.#handleAnimationOpeningEnd.bind(this);
            this.handleAnimationOpeningCancelBound = this.#handleAnimationOpeningCancel.bind(this);

            document.addEventListener('animationstart', this.handleAnimationOpeningStartBound);
            document.addEventListener('animationend', this.handleAnimationOpeningEndBound);
            document.addEventListener('animationcancel', this.handleAnimationOpeningCancelBound);

            // @ts-ignore
            this.root.classList.add('is-animating', 'is-animating--open');
            this.isOpening = true;
            this.isAnimating = true;
        } else {
            this.isOpen = true;
            this.root.dispatchEvent(this.#events.afterOpen);
            this.options.onAfterOpen(this, e);
        }

        this.initDropdownItems();
        this.dispatchStateUpdateEvent();
    }


    /**
     * Handle any when an closing animation ends within or on the this.domNode
     * @param {AnimationEvent} e AnimationEvent object
     */
    #handleAnimationClosingEnd(e){
        if(!this.dropdown) return;

        if( e.target === this.root || this.root.contains(e.target) ){
            if( this.dropdown.contains(e.target) && e.target !== this.dropdown ) return;
            
            this.#animationCloseCount--;

            if (this.#animationCloseCount !== 0) return;

            // @ts-ignore
            this.root.classList.remove('is-animating', 'is-animating--close')
            this.root.classList.remove(this.options.openClass);
            this.dropdown.setAttribute('aria-hidden', 'true');

            this.root.dispatchEvent(this.#events.afterClose);
            this.options.onAfterClose(this, e);
            
            if(this.handleAnimationClosingStartBound) document.removeEventListener('animationstart', this.handleAnimationClosingStartBound);
            if(this.handleAnimationClosingEndBound) document.removeEventListener('animationend', this.handleAnimationClosingEndBound);
            
            this.isOpen = false;
            this.isClosing = false;
            this.isAnimating = false;
        }
    }

    /**
     * Handle when an closing animation starts within or on the this.domNode
     * @param {AnimationEvent} e AnimationEvent object
     */
    #handleAnimationClosingStart(e){
        if(!this.dropdown) return;

        if( e.target === this.root || this.root.contains(e.target) ){
            if( this.dropdown.contains(e.target) && e.target !== this.dropdown ) return;
            this.#animationCloseCount++;
        }
    }


    /**
     * Handle when an closing animation gets cancelled within or on the this.domNode
     * @param {AnimationEvent} e AnimationEvent object
     */
    #handleAnimationClosingCancel(e){
        if(!this.dropdown) return;

        this.#animationOpenCount = 0;
        this.#animationCloseCount = 0;
        // @ts-ignore
        this.root.classList.remove('is-animating', 'is-animating--close');
        this.root.classList.remove(this.options.openClass);
        this.dropdown.setAttribute('aria-hidden', 'true');
        this.isOpen = false;
        this.isClosing = false;
        this.isAnimating = false;
        this.root.dispatchEvent(this.#events.afterClose);
        this.options.onAfterClose(this, e);

        if(this.handleAnimationClosingStartBound) document.removeEventListener('animationstart', this.handleAnimationClosingStartBound);
        if(this.handleAnimationClosingEndBound) document.removeEventListener('animationend', this.handleAnimationClosingEndBound);
        if(this.handleAnimationClosingCancelBound) document.removeEventListener('animationcancel', this.handleAnimationClosingCancelBound);
    }


    /**
     * Closes the dropdown
     * @param  {Event|KeyboardEvent|null} e The event that triggered this method
     */
    closeDropdown(e = null) {
        if(!this.trigger || !this.dropdown) return;
        if(this.isAnimating || this.isOpening) return;

        this.#animationOpenCount = 0;
        this.#animationCloseCount = 0;

        if (this.isOpen) {
            const beforeCloseValue = this.root.dispatchEvent(this.#events.beforeClose);
            if(beforeCloseValue === false) return;
            this.options.onBeforeClose(this, e);

            this.trigger.removeAttribute('aria-expanded');

            if (this.options.awaitCloseAnimation) {
                // @ts-ignore
                this.root.classList.add('is-animating', 'is-animating--close');
                this.isClosing = true;
                this.isAnimating = true;

                this.handleAnimationClosingStartBound = this.#handleAnimationClosingStart.bind(this);
                this.handleAnimationClosingEndBound = this.#handleAnimationClosingEnd.bind(this);
                this.handleAnimationClosingCancelBound = this.#handleAnimationClosingCancel.bind(this);
    
                document.addEventListener('animationstart', this.handleAnimationClosingStartBound);
                document.addEventListener('animationend', this.handleAnimationClosingEndBound);
                document.addEventListener('animationcancel', this.handleAnimationClosingCancelBound);

            } else{
                this.root.classList.remove(this.options.openClass);
                this.root.classList.remove('is-animating', 'is-animating--close');
                this.dropdown.setAttribute('aria-hidden', 'true');

                this.root.dispatchEvent(this.#events.afterClose);
                this.options.onAfterClose(this, e);
                this.isOpen = false;
            }
            
            this.dispatchStateUpdateEvent();
        }

        if(isSet(this.dropdownItems)){
            this.dropdownItems.forEach((item, i) => {
                item.removeEventListener('click', this.itemClickHandlers[i]);
                item.removeEventListener('mouseover', this.itemMouseoverHandlers[i]);
                item.removeEventListener('keydown', this.itemKeydownHandlers[i]);
            });
        }

        this.resetBoundingBox();
        this.removeGlobalListeners();
    }

    /**
     * Handles focus of Dropdown
     */
    #onRootFocusin() {
        this.root.classList.add('has-focus');
    }

    /**
     * Handles lose of focus on Dropdown
     */
    #onRootFocusout() {
        this.root.classList.remove('has-focus');
    }

    /**
     * Handles all Trigger toggle keyboard events
     * @param  {KeyboardEvent|null} e The event that triggered this method
     */
    #onTriggerKeydown(e = null) {
        if(!e || !this.trigger) return;

        const key = e.key;
        let flag = false;

        switch (key) {
            case 'Enter':
                this.openDropdown(e);
                this.checkBoundingBox();
                if(this.options.setFocusOnOpen) this.setFocusToFirstItem();
                flag = true;
            break;

            case 'ArrowDown':
            case 'Down':
                if(this.options.openOnArrowKeydown){
                    this.openDropdown(e);
                    this.checkBoundingBox();
                    if(this.options.setFocusOnOpen) this.setFocusToFirstItem();
                    flag = true;
                }
            break;

            case 'Up':
            case 'ArrowUp':
                if(this.options.openOnArrowKeydown){
                    this.openDropdown(e);
                    this.checkBoundingBox();
                    if(this.options.setFocusOnOpen) this.setFocusToLastItem();
                    flag = true;
                }
            break;

            case 'Esc':
            case 'Escape':
                if(this.isOpen){
                    this.closeDropdown(e);
                    this.trigger.focus();
                    flag = true;
                }else{
                    flag = false;
                }
            break;

            case 'Tab':
                if(this.isOpen){
                    this.closeDropdown(e);
                }
                flag = false;
            break;

            default:
            break;
        }

        if (flag) {
            e.stopPropagation();
            e.preventDefault();
        }
    }

    /**
     * Handles Trigger toggle click event
     * @param  {Event|KeyboardEvent|null} e The event that triggered this method
     */
    #onTriggerClick(e = null) {
        if(!e || !this.trigger) return;

        let flag = false;

        if(this.options.openOnTriggerClick){
            if (this.isOpen) {
                this.closeDropdown(e);
                this.trigger.focus();
            } else {
                this.openDropdown(e);
                this.checkBoundingBox();
                this.setFocusToFirstItem();
            }

            flag = true;
        }

        if(!this.options.openOnTriggerClick && this.options.openOnTriggerKeyup){
            if(isInputable(this.trigger) && this.trigger.value !== ""){
                this.openDropdown(e);
                flag = true;
            }
        }

        if (flag) {
            e.stopPropagation();
            e.preventDefault();
        }
    }

    #onTriggerKeyup(e= null){
        if(!e || !this.trigger) return;

        let flag = false;

        if(this.options.openOnTriggerKeyup){
            if(!this.isOpen){
                this.openDropdown(e);
            }

            flag = true;
        }

        if (flag) {
            e.stopPropagation();
            e.preventDefault();
        }
    }


    /**
     * Handles Trigger toggle hover event
     * @param  {Event|KeyboardEvent|null} e The event that triggered this method
     */
    #onTriggerHover(e = null){
        if(!e || !this.trigger) return;

        if(!this.isOpen && this.options.openOnTriggerHover){
            this.openDropdown(e);
            this.checkBoundingBox();
            //if(this.options.setFocusOnOpen) this.setFocusToFirstItem();

            /** @type {NodeJS.Timeout} */
            let timer;

            /** @param {Event|KeyboardEvent|null} e */
            const closeDropdown = (e) => this.closeDropdown(e);
            const root = this.root;

            function handleDomNodeMouseOver(){
                clearTimeout(timer);
            }

            /** @param {Event|KeyboardEvent|null} e */
            function handleDomNodeMouseOut(e){
                timer = setTimeout(() => {
                    closeDropdown(e);
                    root.removeEventListener('mouseout', handleDomNodeMouseOut);
                    root.removeEventListener('mouseover', handleDomNodeMouseOver);
                }, 300);
            }

            root.addEventListener('mouseout', handleDomNodeMouseOut, false);
            root.addEventListener('mouseover', handleDomNodeMouseOver, false);

            e.stopPropagation();
            e.preventDefault();
        }
    }


    /**
     * Handles keydown events on items
     * @param  {KeyboardEvent|null} e The event that triggered this method
     */
    #onItemKeydown(e = null) {
        if(!e || !this.trigger || !e.currentTarget) return;

        /** @type {HTMLElement} */
        const tgt =  e.currentTarget;
        const key = e.key;
        let flag = false;

        if (e.ctrlKey || e.altKey || e.metaKey) return;

        if (e.shiftKey) {
            // @ts-ignore
            if (isPrintableChar(key) && !isInputable(tgt)) {
                this.#setFocusByFirstChar(tgt, key);
                flag = true;
            } else{
                flag = false;
            }

            if (e.key === 'Tab') {
                if(this.options.tabNavigation){
                    this.#incrementFocus(tgt, "backward");
                } else {
                    this.trigger.focus();
                    this.closeDropdown(e);
                }
                flag = true;
            }
        } else {
            switch (key) {
                case ' ':
                    if(tgt.href){
                        window.location.href = tgt.href;
                    }else{
                        this.#onItemClick(e, tgt);
                    }
                    break;

                case 'Esc':
                case 'Escape':
                    this.closeDropdown(e);
                    this.trigger.focus();
                    flag = true;
                    break;

                case 'Up':
                case 'ArrowUp':
                    if(this.options.arrowNavigation) this.#incrementFocus(tgt, "backward");
                    flag = true;
                    break;

                case 'ArrowDown':
                case 'Down':
                    if(this.options.arrowNavigation) this.#incrementFocus(tgt, "forward");
                    flag = true;
                    break;

                case 'Home':
                case 'PageUp':
                    this.setFocusToFirstItem();
                    flag = true;
                    break;

                case 'End':
                case 'PageDown':
                    this.setFocusToLastItem();
                    flag = true;
                    break;

                case 'Tab':
                    if(this.options.tabNavigation){
                        this.#incrementFocus(tgt, "forward");
                    } else {
                        this.closeDropdown(e);
                        this.trigger.focus();
                    }
                    flag = true;
                    break;
                
                case 'Enter':
                    this.#onItemClick(e, tgt);
                    break;

                default:
                    // @ts-ignore
                    if (isPrintableChar(key) && !isInputable(tgt)) {
                        this.#setFocusByFirstChar(tgt, key);
                        flag = true;
                    } else{
                        flag = false;
                    }
                    break;
            }
        }
        if (flag) {
            e.stopPropagation();
            e.preventDefault();
        }
    }

    /**
     * Handles mouseover events on items
     * @param {*} e 
     */
    #onItemMouseover(e = null) {

    }


    /**
     * Handles click events on items
     * @param  {Event|KeyboardEvent|null} e The event that triggered this method
     */
    #onItemClick(e = null, tgt = null) {
        if (this.options.updateOnItemClick === true) {
            
            if(e && e.currentTarget && this.options.updateSelector) {
                const updateTargets = document.querySelectorAll(this.options.updateSelector);
                updateTargets.forEach(function (updateTarget) {
                    updateTarget.textContent = e.currentTarget.textContent;
                });
            } else{
                if(e && e.currentTarget && this.trigger){
                    this.trigger.querySelector('span').textContent = e.currentTarget.textContent;
                }
            }
            
        }

        if(this.options.closeOnItemClick === true && this.trigger) {
            this.closeDropdown(e);
            this.trigger.focus();
        }

        this.dropdownItems.forEach((item)=>{
            item.classList.remove('is-selected');
            if(item == e.currentTarget){
                item.classList.add('is-selected');
            }
        });

        this.options.onSelection(this, tgt || e.currentTarget, e);
        this.root.dispatchEvent(this.#events.onSelection);
    }


    /**
     * Checks the bounding box of the dropdown element against the window bounds
     * @param {*} y 
     * @param {*} x 
     */
    checkBoundingBox(y = true, x = true) {
        if(!this.dropdown) return;

        let dropdownBounds = this.dropdown.getBoundingClientRect();

        if(y) this.#checkYBounding(dropdownBounds);
        
        if(x) this.#checkXBounding(dropdownBounds);
    }


    /**
     * 
     * @param {*} boundingRect
     */
    #checkYBounding(boundingRect){
        if(boundingRect.bottom > window.innerHeight && boundingRect.top < 0){
            this.root.setAttribute('collision-y', 'both');
            return;
        }

        if(boundingRect.bottom > window.innerHeight) {
            this.root.setAttribute('collision-y', 'bottom');
            return;
        }
    
        if(boundingRect.top < 0) {
            this.root.setAttribute('collision-y', 'top');
            return;
        }
    }


    /**
     * 
     * @param {*} boundingRect 
     */
    #checkXBounding(boundingRect){

    }


    /**
     * Resets any class name changes by checkBoundingBox()
     * @param {*} y 
     * @param {*} x 
     */
    resetBoundingBox(y = true, x = true) {
        if(y) this.root.removeAttribute('collision-y');
        if(x) this.root.removeAttribute('collision-x');
    }


    /**
     * Handles click events that are outside of the dropdown remit
     * @param  {Event|KeyboardEvent|null} e The event that triggered this method
     */
    #onBackgroundMousedown(e = null) {
        if(!e || !this.trigger || !this.dropdown) return;

        if (!this.root.contains(e.target) && this.options.closeOnBackgroundClick) {
            if (this.isOpen) {
                this.closeDropdown(e);
                this.trigger.focus();
            }
        }
    }
}

export { Dropdown }